import React, { useState, useEffect } from 'react';
import { API_BASE } from '../../lib/config';
import {
  ArrowLeftIcon,
  DocumentTextIcon,
  PencilIcon,
  CheckCircleIcon
} from '@heroicons/react/24/outline';
import toast from 'react-hot-toast';

const API = API_BASE;

const ContentEditManagement = ({ onBack }) => {
  const [contentItems] = useState([
    // Email verification templates (admin-editable, reflected immediately)
    {
      id: 'email-verify-subject',
      label: '認証メール: 件名',
      description: '件名のみ。例: [Byters] アカウント登録のご確認（メール認証のお願い）'
    },
    {
      id: 'email-verify-html',
      label: '認証メール: HTML本文',
      description: 'HTML可。必ず {{VERIFY_URL}} を本文に含めてください（認証リンクに置換されます）'
    },
    {
      id: 'email-verify-text',
      label: '認証メール: テキスト本文',
      description: 'テキスト版。必ず {{VERIFY_URL}} を含めてください'
    },
    {
      id: 'privacy-policy',
      label: 'プライバシーポリシー',
      description: '個人情報の取り扱いに関する方針'
    },
    {
      id: 'terms',
      label: '利用規約',
      description: 'サービス利用に関する規約'
    },
    {
      id: 'faq',
      label: 'よくある質問',
      description: 'FAQ（任意で自由記述に差し替え可能）'
    },
    {
      id: 'job-categories',
      label: '取扱職種の範囲',
      description: '対応している職種の詳細'
    },
    {
      id: 'join-team',
      label: 'バイターズを一緒に作りませんか？',
      description: '採用・協力者募集のメッセージ'
    }
  ]);

  const [selectedItem, setSelectedItem] = useState(null);
  const [editingContent, setEditingContent] = useState({ title: '', content: '' });
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  // Static defaults used to prefill when CMS is empty
  const DEFAULTS = {
    // Email verification templates (used if CMS empty)
    'email-verify-subject': {
      title: '認証メール 件名',
      content: '[Byters] アカウント登録のご確認（メール認証のお願い）'
    },
    'email-verify-html': {
      title: '認証メール HTML',
      content: `
        <table role="presentation" cellspacing="0" cellpadding="0" border="0" width="100%" style="background:#f8fafc;padding:24px 0;"><tr><td align="center">
          <table role="presentation" cellspacing="0" cellpadding="0" border="0" width="560" style="background:#ffffff;border-radius:12px;box-shadow:0 4px 12px rgba(15,23,42,0.06);padding:28px;font-family:system-ui,-apple-system,Segoe UI,Roboto,Helvetica,Arial,sans-serif;color:#0f172a;">
            <tr><td style="text-align:center;padding-bottom:12px;"><div style="font-weight:700;font-size:18px;">Byters</div></td></tr>
            <tr><td style="font-size:15px;line-height:1.7;">
              <p style="margin:0 0 8px;">ご登録ありがとうございます。</p>
              <p style="margin:0 0 16px;">セキュリティ保護のため、メール認証を完了してください。</p>
              <div style="margin:16px 0 20px;">
                <a href="{{VERIFY_URL}}" style="display:inline-block;background:#0ea5e9;color:#ffffff;text-decoration:none;padding:12px 18px;border-radius:8px;font-weight:600;">メール認証する</a>
              </div>
              <p style="margin:0 0 8px;color:#475569;">ボタンが開けない場合は、次のURLをブラウザに貼り付けてください：</p>
              <p style="word-break:break-all;margin:0;color:#0ea5e9;"><a href="{{VERIFY_URL}}" style="color:#0ea5e9;">{{VERIFY_URL}}</a></p>
              <hr style="border:none;border-top:1px solid #e2e8f0;margin:20px 0;" />
              <p style="margin:0;color:#64748b;font-size:13px;">このメールに心当たりがない場合は破棄してください。</p>
              <p style="margin:4px 0 0;color:#64748b;font-size:13px;">お問い合わせ：support@byters.jp</p>
            </td></tr>
          </table>
        </td></tr></table>
      `
    },
    'email-verify-text': {
      title: '認証メール テキスト',
      content: `Byters にご登録ありがとうございます。
セキュリティ保護のため、以下のURLからメール認証を完了してください。

{{VERIFY_URL}}

このメールにお心当たりがない場合は破棄してください。
お問い合わせ: support@byters.jp`
    },
    'privacy-policy': {
      title: 'バイターズ プライバシーポリシー',
      content: `
        <h2>1. 目的</h2>
        <p>本プライバシーポリシーは、当社が提供するサービスにおける、ユーザーの個人情報の取り扱い方針を定めるものです。関連法令・ガイドラインを遵守し、適正に個人情報を取り扱います。</p>

        <h2>2. 取得する情報</h2>
        <ul>
          <li>アカウント情報（氏名、メールアドレス、生年月日、電話番号 等）</li>
          <li>プロフィール・就労関連情報（居住エリア、希望職種、保有資格 等）</li>
          <li>本人確認に関する情報（提出書類、撮影画像 等）</li>
          <li>決済・振込に関する情報（ウォレット残高、取引履歴、銀行口座情報 等）</li>
          <li>端末・ブラウザ情報、クッキー、IPアドレス、位置情報（許諾時）</li>
          <li>お問い合わせ内容、アンケート回答、各種ログ</li>
        </ul>

        <h2>3. 利用目的</h2>
        <ul>
          <li>サービスの提供、本人確認、不正利用の防止</li>
          <li>求人情報の提示、マッチング、就労管理、報酬の精算</li>
          <li>各種連絡、サポート対応、重要なお知らせの配信</li>
          <li>サービス品質向上、新機能開発、利便性改善のための分析</li>
          <li>法令等に基づく対応</li>
        </ul>

        <h2>4. クッキー等の利用</h2>
        <p>利便性向上および利用状況の把握のため、クッキーや類似技術を使用します。ブラウザ設定で無効化できますが、機能が制限される場合があります。</p>

        <h2>5. 第三者提供・委託</h2>
        <p>法令に基づく場合、同意を得た場合、または業務委託に必要な範囲で、適切な管理の下で個人情報を第三者に提供・委託することがあります。</p>

        <h2>6. セキュリティ管理</h2>
        <p>個人情報の漏えい、滅失または毀損の防止等のため、必要かつ適切な安全管理措置を講じます。</p>

        <h2>7. 保有期間</h2>
        <p>利用目的の達成に必要な範囲で適切に保有し、保存期間の経過または必要がなくなった場合には、適切な方法で消去・廃棄します。</p>

        <h2>8. ユーザーの権利</h2>
        <p>ユーザーは、当社が保有する自己の個人情報について、開示・訂正・利用停止・削除等を求めることができます。</p>

        <h2>9. 未成年の利用</h2>
        <p>未成年のユーザーがサービスを利用する場合は、保護者等の同意を得た上でご利用ください。</p>

        <h2>10. 改定</h2>
        <p>本ポリシーは必要に応じて改定されることがあります。重要な変更がある場合は、アプリ内通知等によりお知らせします。</p>

        <h2>11. お問い合わせ</h2>
        <p>本ポリシーに関するご質問は、マイページ「お問い合わせ」よりご連絡ください。</p>
      `
    },
    'terms': {
      title: 'バイターズ利用規約',
      content: `
        <h2>第1条（適用）</h2>
        <p>この利用規約（以下「本規約」）は、当社が提供する本サービスの利用条件を定めるものです。ユーザーは本規約に従い本サービスを利用します。</p>

        <h2>第2条（利用登録）</h2>
        <p>本サービスの利用を希望する方は、本規約に同意の上、当社の定める方法によって利用登録を申請するものとします。当社は審査の上、登録の可否を判断します。</p>

        <h2>第3条（ユーザーIDおよびパスワードの管理）</h2>
        <ul>
          <li>ユーザーは自己の責任においてユーザーIDおよびパスワードを適切に管理するものとします。</li>
          <li>第三者への譲渡・貸与・共用は禁止します。</li>
        </ul>

        <h2>第4条（利用料金および支払方法）</h2>
        <p>基本利用は無料ですが、一部機能は有料の場合があります。勤務完了後の報酬精算は当社所定のルールに従います。</p>

        <h2>第5条（禁止事項）</h2>
        <ul>
          <li>法令または公序良俗に違反する行為、犯罪行為</li>
          <li>虚偽情報の登録、なりすまし</li>
          <li>知的財産権・プライバシーの侵害</li>
          <li>本サービスの運営を妨げる行為</li>
          <li>不当なキャンセル、直取引の勧誘 等</li>
        </ul>

        <h2>第6条（本サービスの提供の停止等）</h2>
        <p>システム保守、不可抗力等の事由により、事前通知なく本サービスの全部または一部を停止または中断することがあります。</p>

        <h2>第7条（利用制限および登録抹消）</h2>
        <p>ユーザーが本規約に違反した場合等、当社は投稿データの削除や利用制限、登録抹消等の措置を講じることができます。</p>

        <h2>第8条（退会）</h2>
        <p>ユーザーは当社所定の手続きにより退会できます。退会後も性質上存続すべき条項は有効に存続します。</p>

        <h2>第9条（保証の否認および免責事項）</h2>
        <p>当社は、本サービスに事実上または法律上の瑕疵がないことを保証しません。当社の故意又は重大な過失がある場合を除き、当社は損害賠償責任を負いません。</p>

        <h2>第10条（サービス内容の変更等）</h2>
        <p>当社は、ユーザーに通知することなく、本サービスの内容を変更しまたは提供を中止することができます。</p>

        <h2>第11条（規約の変更）</h2>
        <p>当社は必要に応じて本規約を変更できます。変更後に本サービスを利用した場合、変更後の規約に同意したものとみなします。</p>

        <h2>第12条（個人情報の取扱い）</h2>
        <p>当社は「プライバシーポリシー」に従い個人情報を適切に取り扱います。</p>

        <h2>第13条（通知または連絡）</h2>
        <p>ユーザーと当社との間の通知または連絡は、当社の定める方法によって行うものとします。当社は、ユーザーから別途定める方式に従った変更届け出がない限り、現在登録されている連絡先が有効なものとみなして当該連絡先へ通知または連絡を行い、これらは、発信時にユーザーへ到達したものとみなします。</p>

        <h2>第14条（権利義務の譲渡の禁止）</h2>
        <p>ユーザーは、当社の書面による事前の承諾なく、利用契約上の地位または本規約に基づく権利もしくは義務を第三者に譲渡し、または担保に供することはできません。</p>

        <h2>第15条（準拠法・裁判管轄）</h2>
        <p>本規約は日本法に準拠し、本サービスに関して紛争が生じた場合には、当社の本店所在地を管轄する裁判所を第一審の専属的合意管轄とします。</p>

        <h2>お問い合わせ</h2>
        <div>
          <p>本規約に関するお問い合わせは、以下までご連絡ください。</p>
          <p>
            <strong>株式会社バイターズ</strong><br />
            メール：legal@baiters.co.jp<br />
            電話：03-1234-5678（平日10:00-18:00）
          </p>
        </div>
      `
    },
    'job-categories': {
      title: '取扱職種の範囲',
      content: `
        <h2>対象となるお仕事</h2>
        <p>短時間・単発での就業が可能なお仕事を中心に、以下カテゴリの範囲で取り扱っています。安全性・適法性・就業環境を考慮し、当社基準により掲載を審査します。</p>
        <h2>主なカテゴリ</h2>
        <ul>
          <li>飲食・フード（ホール・接客／キッチン補助／洗い場／デリバリー／製造補助）</li>
          <li>販売・小売（レジ・接客／品出し／バックヤード／試食・試飲販売）</li>
          <li>イベント・キャンペーン（運営サポート／設営・撤去／受付／サンプリング）</li>
          <li>物流・軽作業（仕分け／ピッキング／梱包／検品／搬入・搬出）</li>
          <li>オフィス・事務補助（データ入力／ファイリング／スキャン・庶務）</li>
          <li>清掃・メンテナンス（施設清掃／客室清掃／売場清掃）</li>
        </ul>
        <h2>取扱い対象外の例</h2>
        <ul>
          <li>違法性のある業務、風営法等で制限される業務</li>
          <li>高度な専門資格・免許が必要な業務（医療・法律実務 等）</li>
          <li>危険物の取り扱い等、特別な安全管理が必要な業務</li>
        </ul>
        <h2>留意事項</h2>
        <ul>
          <li>求人内容は事業者の責任で掲載されます。当社は適切性の確認に努めますが、すべての内容を保証するものではありません。</li>
          <li>就業条件（賃金、時間、場所 等）は各求人詳細をご確認ください。</li>
          <li>未成年の方は、保護者等の同意のもとでご利用ください。</li>
        </ul>
      `
    },
    'join-team': {
      title: 'バイターズを一緒につくりませんか？',
      content: `
        <p>バイターズのプロダクト/運営を一緒に作っていただける仲間を募集しています。プロダクト開発、デザイン、カスタマーサポート、事業開発など、幅広い領域で参画いただけます。</p>
        <ul>
          <li>短時間・副業からの参画歓迎</li>
          <li>フルリモート可</li>
          <li>ユーザー体験を第一に考えられる方</li>
        </ul>
        <p>ご関心のある方は、マイページのお問い合わせからご連絡ください。一緒により良い体験を届けましょう。</p>
      `
    },
    'faq': {
      title: 'よくある質問',
      content: `
        <h2>アカウント・登録</h2>
        <ul>
          <li>バイターズは単発から短期の仕事を見つけるマッチングサービスです。</li>
          <li>登録は無料です。本人確認には運転免許証、パスポート、マイナンバーカード等が利用できます。</li>
        </ul>
        <h2>お仕事について</h2>
        <ul>
          <li>主な業種：カフェ、コンビニ、イベント、清掃、配送・配達など。</li>
          <li>当日キャンセルは原則不可。やむを得ない事情以外は控えてください（ペナルティ対象になる場合があります）。</li>
        </ul>
        <h2>報酬・支払い</h2>
        <ul>
          <li>勤務完了後、事業者の確認後にウォレットへ反映（通常2-3営業日）。</li>
          <li>ウォレットからの引き出しは口座登録後に可能（手数料あり）。</li>
        </ul>
        <h2>アプリの使い方</h2>
        <ul>
          <li>通知が届かない場合は、端末の通知設定とアプリ内設定をご確認ください。</li>
        </ul>
        <h2>トラブル・困ったとき</h2>
        <ul>
          <li>事業者とのトラブルは「お問い合わせ」からご連絡ください。当社がサポートします。</li>
          <li>パスワードを忘れた場合は、ログイン画面のリセットから手続きしてください。</li>
          <li>退会は「退会手続き」から実施できます。未引き出し報酬がある場合は先に引き出してください。</li>
        </ul>
      `
    }
  };

  // 既存の有無に関係なく、全項目を初期文面で上書き
  const forceSyncDefaults = async () => {
    const token = localStorage.getItem('admin_token');
    if (!token) {
      toast.error('管理者としてログインしてください');
      return;
    }
    if (!window.confirm('全ての項目を初期文面で上書きします。よろしいですか？')) return;
    toast.loading('初期文面で上書き同期中...', { id: 'force-sync' });
    let updated = 0;
    try {
      for (const item of contentItems) {
        const id = item.id;
        const fallback = DEFAULTS[id] || { title: item.label, content: '' };
        const put = await fetch(`${API}/admin/content/${id}`, {
          method: 'PUT',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({
            title: fallback.title || item.label,
            content: fallback.content || '',
            content_type: id
          })
        });
        if (put.ok) updated++; else toast.error(`${item.label} の上書きに失敗しました`);
      }
      toast.success(`上書き同期完了: 反映 ${updated} 件`, { id: 'force-sync' });
    } catch (e) {
      console.error(e);
      toast.error('上書き同期中にエラーが発生しました', { id: 'force-sync' });
    }
  };

  // 選択中の項目を初期文面に戻す（保存まで実施）
  const restoreSelectedToDefault = async () => {
    if (!selectedItem) return;
    const token = localStorage.getItem('admin_token');
    if (!token) {
      toast.error('管理者としてログインしてください');
      return;
    }
    const fallback = DEFAULTS[selectedItem] || { title: contentItems.find(i=>i.id===selectedItem)?.label || '', content: '' };
    setEditingContent({ title: fallback.title, content: fallback.content });
    try {
      setSaving(true);
      const res = await fetch(`${API}/admin/content/${selectedItem}`, {
        method: 'PUT',
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ title: fallback.title, content: fallback.content, content_type: selectedItem })
      });
      if (res.ok) toast.success('初期文面に戻しました'); else toast.error('初期文面への復元に失敗しました');
    } catch (e) {
      console.error(e);
      toast.error('復元中にエラーが発生しました');
    } finally {
      setSaving(false);
    }
  };

  // 本番CMSが空の項目だけ、DEFAULTSを一括投入
  const syncDefaults = async () => {
    const token = localStorage.getItem('admin_token');
    if (!token) {
      toast.error('管理者としてログインしてください');
      return;
    }
    toast.loading('初期文面の同期を開始します...', { id: 'sync' });
    let updated = 0;
    let skipped = 0;
    try {
      for (const item of contentItems) {
        const id = item.id;
        const resp = await fetch(`${API}/admin/content/${id}`, {
          headers: { 'Authorization': `Bearer ${token}` }
        });
        let needSeed = true;
        if (resp.ok) {
          const data = await resp.json();
          const hasTitle = !!(data.title && data.title.trim());
          const hasContent = !!(data.content && data.content.trim());
          if (hasTitle || hasContent) needSeed = false;
        }
        if (!needSeed) { skipped++; continue; }
        const fallback = DEFAULTS[id] || { title: item.label, content: '' };
        const put = await fetch(`${API}/admin/content/${id}`, {
          method: 'PUT',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({
            title: fallback.title || item.label,
            content: fallback.content || '',
            content_type: id
          })
        });
        if (put.ok) updated++; else toast.error(`${item.label} の同期に失敗しました`);
      }
      toast.success(`同期完了: 反映 ${updated} 件 / 既存のためスキップ ${skipped} 件`, { id: 'sync' });
    } catch (e) {
      console.error(e);
      toast.error('同期中にエラーが発生しました', { id: 'sync' });
    }
  };

  // 選択されたコンテンツの詳細を取得
  const fetchContentDetail = async (contentId) => {
    try {
      setLoading(true);
      const token = localStorage.getItem('admin_token');
      
      const response = await fetch(`${API}/admin/content/${contentId}`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (response.ok) {
        const data = await response.json();
        const fallback = DEFAULTS[contentId] || { title: '', content: '' };
        setEditingContent({
          title: (data.title && data.title.trim()) ? data.title : (fallback.title || (contentItems.find(i=>i.id===contentId)?.label || '')),
          content: (data.content && data.content.trim()) ? data.content : (fallback.content || '')
        });
      } else {
        // コンテンツが存在しない場合は空の内容で初期化
        const fallback = DEFAULTS[contentId] || {};
        setEditingContent({
          title: fallback.title || contentItems.find(item => item.id === contentId)?.label || '',
          content: fallback.content || ''
        });
      }
    } catch (error) {
      console.error('Error fetching content:', error);
      toast.error('コンテンツの取得に失敗しました');
    } finally {
      setLoading(false);
    }
  };

  // コンテンツを保存
  const saveContent = async () => {
    if (!selectedItem || !editingContent.title.trim()) {
      toast.error('タイトルは必須です');
      return;
    }

    try {
      setSaving(true);
      const token = localStorage.getItem('admin_token');
      
      const response = await fetch(`${API}/admin/content/${selectedItem}`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          title: editingContent.title,
          content: editingContent.content,
          content_type: selectedItem
        })
      });

      if (response.ok) {
        toast.success('コンテンツを保存しました');
      } else {
        toast.error('コンテンツの保存に失敗しました');
      }
    } catch (error) {
      console.error('Error saving content:', error);
      toast.error('保存中にエラーが発生しました');
    } finally {
      setSaving(false);
    }
  };

  // コンテンツ選択時の処理
  const handleItemSelect = (itemId) => {
    setSelectedItem(itemId);
    fetchContentDetail(itemId);
  };

  // 一覧に戻る
  const handleBackToList = () => {
    setSelectedItem(null);
    setEditingContent({ title: '', content: '' });
  };

  if (selectedItem) {
    // 編集画面
    return (
      <div className="space-y-6">
        {/* 戻るボタン */}
        <div className="flex items-center">
          <button
            onClick={handleBackToList}
            className="flex items-center text-gray-600 hover:text-gray-800 transition-colors"
          >
            <ArrowLeftIcon className="w-5 h-5 mr-2" />
            一覧に戻る
          </button>
        </div>

        {/* ヘッダー */}
        <div className="flex items-center">
          <PencilIcon className="w-8 h-8 text-blue-600 mr-3" />
          <div>
            <h1 className="text-2xl font-bold text-gray-900">
              {contentItems.find(item => item.id === selectedItem)?.label}
            </h1>
            <p className="text-sm text-gray-600 mt-1">
              {contentItems.find(item => item.id === selectedItem)?.description}
            </p>
          </div>
        </div>

        {loading ? (
          <div className="text-center py-8">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto"></div>
            <p className="mt-2 text-gray-600">読み込み中...</p>
          </div>
        ) : (
          <div className="bg-white rounded-lg shadow-sm border border-gray-200">
            <div className="p-6">
              <div className="space-y-6">
                {/* タイトル編集 */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    タイトル <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    value={editingContent.title}
                    onChange={(e) => setEditingContent(prev => ({ ...prev, title: e.target.value }))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    placeholder="タイトルを入力してください"
                  />
                </div>

                {/* 本文編集 */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    本文内容
                  </label>
                  <textarea
                    value={editingContent.content}
                    onChange={(e) => setEditingContent(prev => ({ ...prev, content: e.target.value }))}
                    rows="20"
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    placeholder="本文内容を入力してください&#10;&#10;・HTMLタグも使用できます&#10;・改行は自動で反映されます"
                  />
                  <p className="text-sm text-gray-500 mt-2">
                    HTMLタグを使用してテキストの装飾ができます（例：&lt;h2&gt;見出し&lt;/h2&gt;、&lt;p&gt;段落&lt;/p&gt;、&lt;strong&gt;強調&lt;/strong&gt;）
                  </p>
                </div>

                {/* 保存ボタン */}
                <div className="flex gap-4">
                  <button
                    onClick={saveContent}
                    disabled={saving || !editingContent.title.trim()}
                    className="flex items-center px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                  >
                    <CheckCircleIcon className="w-4 h-4 mr-2" />
                    {saving ? '保存中...' : '保存'}
                  </button>
                  <button
                    type="button"
                    onClick={restoreSelectedToDefault}
                    disabled={saving}
                    className="px-4 py-2 bg-gray-100 text-gray-800 rounded-lg border border-gray-300 hover:bg-gray-200 disabled:opacity-50"
                  >
                    初期文面に戻す
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    );
  }

  // 一覧画面
  return (
    <div className="space-y-6">
      {/* 戻るボタン */}
      <div className="flex items-center">
        <button
          onClick={onBack}
          className="flex items-center text-gray-600 hover:text-gray-800 transition-colors"
        >
          <ArrowLeftIcon className="w-5 h-5 mr-2" />
          戻る
        </button>
      </div>

      {/* ヘッダー */}
      <div className="flex items-center">
        <DocumentTextIcon className="w-8 h-8 text-blue-600 mr-3" />
        <div>
          <h1 className="text-2xl font-bold text-gray-900">コンテンツ編集</h1>
          <p className="text-sm text-gray-600 mt-1">
            求職者向けページのコンテンツを編集できます
          </p>
        </div>
      </div>

      {/* 初期文面 一括同期ボタン */}
      <div>
        <button
          onClick={syncDefaults}
          className="px-4 py-2 bg-blue-50 text-blue-700 border border-blue-200 rounded hover:bg-blue-100 transition-colors text-sm"
        >
          初期文面で本番へ同期（未登録の項目のみ）
        </button>
        <button
          onClick={forceSyncDefaults}
          className="ml-3 px-4 py-2 bg-red-50 text-red-700 border border-red-200 rounded hover:bg-red-100 transition-colors text-sm"
        >
          初期文面で本番へ上書き同期（全項目）
        </button>
      </div>

      {/* コンテンツ一覧 */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {contentItems.map((item) => (
          <div
            key={item.id}
            className="bg-white rounded-lg shadow-sm border border-gray-200 hover:shadow-md transition-shadow cursor-pointer"
            onClick={() => handleItemSelect(item.id)}
          >
            <div className="p-6">
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center">
                  <DocumentTextIcon className="w-6 h-6 text-gray-400 mr-3" />
                  <h3 className="text-lg font-semibold text-gray-900">{item.label}</h3>
                </div>
                <PencilIcon className="w-5 h-5 text-blue-500" />
              </div>
              <p className="text-gray-600 text-sm">{item.description}</p>
              <div className="mt-4">
                <span className="inline-block bg-blue-100 text-blue-800 text-xs px-2 py-1 rounded">
                  クリックで編集
                </span>
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* 注意事項 */}
      <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
        <h4 className="text-sm font-medium text-yellow-800 mb-2">注意事項</h4>
        <ul className="text-sm text-yellow-700 space-y-1">
          <li> 編集内容は即座に求職者画面に反映されます</li>
          <li> HTMLタグを使用する場合は正しい形式で入力してください</li>
          <li> 重要な変更の場合は事前に関係者への連絡をお勧めします</li>
        </ul>
      </div>
    </div>
  );
};

export default ContentEditManagement;